/**
 * Timezone conversion utilities
 * Handles conversion between UTC and various timezones for dates and times
 */

import { getIntlTimeFormatOptions } from '@/config/hoursFormat';
import { getIanaTimezoneAbbreviation } from '@/utils/stateTimezone';

// `Intl.DateTimeFormat` construction re-parses ICU locale/pattern data from
// scratch every call and is expensive (measured: dominant cost in slot
// generation under heavy repeated use). This file calls `new
// Intl.DateTimeFormat(...)` with a small, recurring set of (locale, options)
// combinations, so a formatter can always be safely reused — formatters are
// stateless. Cache and return the same instance per (locale, options) key.
const dateTimeFormatCache = new Map<string, Intl.DateTimeFormat>();
function getCachedDateTimeFormat(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = dateTimeFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateTimeFormatCache.set(key, formatter);
  }
  return formatter;
}

/**
 * Timezone abbreviation for an IANA zone at a given instant (EST/EDT, AKST/AKDT, etc.).
 */
export const getTimezoneAbbreviation = (
  timezone: string | null | undefined,
  at: Date = new Date(),
): string => {
  if (!timezone) return '';

  const fromIntl = getIanaTimezoneAbbreviation(timezone, at);
  if (fromIntl) return fromIntl;

  const timezoneMap: Record<string, string> = {
    UTC: 'UTC',
    'America/New_York': 'EST',
    'America/Chicago': 'CST',
    'America/Denver': 'MST',
    'America/Los_Angeles': 'PST',
    'America/Anchorage': 'AKST',
    'Pacific/Honolulu': 'HST',
    'Europe/London': 'GMT',
    'Europe/Paris': 'CET',
    'Asia/Tokyo': 'JST',
    'Asia/Shanghai': 'CST',
  };

  return timezoneMap[timezone] || '';
};

/**
 * Extract date part from ISO datetime strings or date strings
 * Handles PostgreSQL DATE columns that may be returned as UTC datetime strings
 */
export const extractDatePart = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  
  // If already in YYYY-MM-DD format, return as-is
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
  
  // If it's an ISO datetime string, we need to handle PostgreSQL DATE columns
  // that may be returned as UTC datetime strings (e.g., '2025-12-17' becomes '2025-12-16T18:30:00.000Z')
  if (dateStr.includes('T')) {
    const utcDate = new Date(dateStr);
    const utcHours = utcDate.getUTCHours();
    const utcMinutes = utcDate.getUTCMinutes();
    
    // If the time is 18:30 or later in UTC, it's likely representing midnight in IST (UTC+5:30)
    // So the original date is the next day
    // For other timezones, if time is >= 12:00, it might be representing a date in a positive timezone
    // We'll check if the extracted date part is significantly different from what we'd expect
    let extractedDate = dateStr.split('T')[0];
    
    // If the UTC time is >= 18:00, it's likely representing midnight in a timezone ahead of UTC
    // In this case, the actual date might be one day later
    if (utcHours >= 18) {
      // Add one day to get the correct date
      const nextDay = new Date(utcDate);
      nextDay.setUTCDate(nextDay.getUTCDate() + 1);
      const year = nextDay.getUTCFullYear();
      const month = String(nextDay.getUTCMonth() + 1).padStart(2, '0');
      const day = String(nextDay.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    return extractedDate;
  }
  
  // If it has a space (e.g., "2025-12-17 00:00:00"), extract date part
  if (dateStr.includes(' ')) {
    return dateStr.split(' ')[0];
  }
  
  return dateStr;
};

/**
 * Resolve a stored date value to its civil date (YYYY-MM-DD), matching the
 * convention used by the moderator-availability **display label** (`extractDatePart`).
 *
 * The label and calendar grid must agree, so any range filter on
 * `effective_from` / `effective_to` should use the same date extraction as the
 * label the user sees. We delegate to `extractDatePart` (which includes the legacy
 * `>= 18 UTC hour → +1 day` heuristic) so the calendar/booking surfaces stay
 * consistent with the moderator-availability summary row.
 *
 * The `timeZone` parameter is kept for API compatibility but is intentionally unused.
 */
export const extractDatePartInTimezone = (
  dateStr: string | null | undefined,
  _timeZone?: string,
): string => extractDatePart(dateStr);

/** Next calendar day YYYY-MM-DD (UTC date arithmetic). */
export const addOneCalendarDayYmd = (ymd: string): string => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + 1));
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

/** Add N calendar days to YYYY-MM-DD (Gregorian, civil date math). */
export const addCalendarDaysYmd = (ymd: string, delta: number): string => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + delta));
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

/** Monday = 0 … Sunday = 6 for a civil YYYY-MM-DD (UTC calendar date). */
export const weekdayMonday0FromYmd = (ymd: string): number => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return 0;
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const sun0 = dt.getUTCDay();
  return (sun0 + 6) % 7;
};

/** Calendar date (YYYY-MM-DD) for an instant in an IANA timezone. */
export const getYmdInTimezone = (date: Date, timeZone: string): string => {
  try {
    return getCachedDateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
};

/**
 * UTC milliseconds for a wall-clock time on a civil date in a given IANA zone.
 * Used to place calendar slots and sessions in the viewer's chosen timezone.
 */
export const utcMsForZonedWallClock = (
  ymd: string,
  hours: number,
  minutes: number,
  timeZone: string,
): number | null => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [year, month, day] = ymd.split('-').map(Number);
  const targetTotalMinutes = hours * 60 + minutes;
  let testDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const sourceFormatter = getCachedDateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const dateFormatter = getCachedDateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  for (let i = 0; i < 4; i++) {
    const sourceParts = sourceFormatter.formatToParts(testDate);
    const sourceHour = Number.parseInt(sourceParts.find((p) => p.type === 'hour')?.value || '0', 10);
    const sourceMin = Number.parseInt(sourceParts.find((p) => p.type === 'minute')?.value || '0', 10);
    const sourceTotalMinutes = sourceHour * 60 + sourceMin;
    const offsetMinutes = targetTotalMinutes - sourceTotalMinutes;
    testDate = new Date(testDate.getTime() + offsetMinutes * 60 * 1000);
  }
  if (dateFormatter.format(testDate) !== ymd) {
    return null;
  }
  return testDate.getTime();
};

/** Anchor civil date where getUTCDay() equals stored `day_of_week` (Sunday = 0). */
export function refDateForStoredWeekday(
  effectiveFromYmd: string,
  targetDow: number,
): string {
  const from = extractDatePart(effectiveFromYmd);
  if (!from || !/^\d{4}-\d{2}-\d{2}$/.test(from)) return from;
  const [y, m, d] = from.split('-').map(Number);
  const start = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const startDow = start.getUTCDay();
  const delta = (targetDow - startDow + 7) % 7;
  start.setUTCDate(start.getUTCDate() + delta);
  const yy = start.getUTCFullYear();
  const mm = String(start.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(start.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Build a UTC ISO instant from a civil date and HH:mm stored as UTC wall clock. */
export function composeUtcIsoFromYmdHm(ymd: string, hhmm: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const [h, min] = String(hhmm || '00:00')
    .split(':')
    .slice(0, 2)
    .map(Number);
  return new Date(Date.UTC(y, m - 1, d, h || 0, min || 0, 0)).toISOString();
}

/** The wall-clock HH:mm a UTC instant reads as in a given IANA timezone. */
export function wallClockHmInTimezone(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return `${get('hour')}:${get('minute')}`;
}

/** The calendar date (YYYY-MM-DD) a UTC instant falls on, in a given IANA timezone. */
export function ymdInTimezone(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * The UTC instant a wall-clock HH:mm on a given date represents in a specific
 * IANA timezone. Two passes correct for the guess's own DST offset, matching
 * the iterative technique `convertTimeToUTC` above already uses — needed here
 * (rather than reusing that helper) because it must return a full instant,
 * including any day rollover, not just a time string.
 */
export function zonedWallClockToUtcDate(ymd: string, hm: string, timeZone: string): Date {
  const [year, month, day] = ymd.split('-').map(Number);
  const [hour, minute] = hm.split(':').map(Number);
  let guess = new Date(Date.UTC(year, month - 1, day, hour || 0, minute || 0, 0));
  for (let i = 0; i < 2; i++) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(guess);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
    const asIfUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
    const target = Date.UTC(year, month - 1, day, hour || 0, minute || 0);
    guess = new Date(guess.getTime() + (target - asIfUtc));
  }
  return guess;
}

const hmStringToMinutes = (hm: string): number => {
  const [h, m] = hm.split(':').map((n) => Number.parseInt(n, 10));
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

export const minutesToHmString = (mins: number): string => {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export type AvailabilityRowLike = {
  effective_from?: string | null;
  effective_to?: string | null;
  day_of_week?: number | null;
  start_time?: string | null;
  end_time?: string | null;
};

/**
 * Decode availability rows saved as UTC wall clock (ModeratorWeeklyAvailabilityCard)
 * into start/end minutes on a calendar column day in the viewer timezone.
 * Matches the list/weekly editor read path so grid blocks align with configured hours.
 */
export function availabilityRowToMinutesOnCalendarDay(
  record: AvailabilityRowLike,
  columnDayYmd: string,
  calendarViewTimezone: string,
): { startMinutes: number; endMinutes: number } | null {
  if (!columnDayYmd || !/^\d{4}-\d{2}-\d{2}$/.test(columnDayYmd)) {
    return null;
  }

  const dow = Number(record.day_of_week);
  if (Number.isNaN(dow) || dow < 0 || dow > 6) {
    return null;
  }

  const startUtcHm = String(record.start_time || '').slice(0, 5);
  const endUtcHm = String(record.end_time || '').slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(startUtcHm) || !/^\d{2}:\d{2}$/.test(endUtcHm)) {
    return null;
  }

  // Anchor UTC wall times on the calendar column date so recurring weekly rows
  // render on every in-range week, not only the first week after effective_from.
  const anchorUtcYmd = columnDayYmd;
  const startUtcIso = composeUtcIsoFromYmdHm(anchorUtcYmd, startUtcHm);
  if (new Date(startUtcIso).getUTCDay() !== dow) {
    return null;
  }
  const endAnchorUtcYmd =
    hmStringToMinutes(endUtcHm) <= hmStringToMinutes(startUtcHm)
      ? addCalendarDaysYmd(anchorUtcYmd, 1)
      : anchorUtcYmd;
  const endUtcIso = composeUtcIsoFromYmdHm(endAnchorUtcYmd, endUtcHm);

  const startLocal = utcIsoToDateAndTimeInTimezone(
    startUtcIso,
    calendarViewTimezone,
  );
  const endLocal = utcIsoToDateAndTimeInTimezone(endUtcIso, calendarViewTimezone);
  if (!startLocal || !endLocal) return null;

  return minutesForColumnDaySegment(
    columnDayYmd,
    startLocal.date,
    startLocal.time,
    endLocal.date,
    endLocal.time,
  );
}

/**
 * Given the local start/end date+time of an availability span, compute the
 * minutes range that falls on the requested calendar column day.
 */
function minutesForColumnDaySegment(
  columnDayYmd: string,
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
): { startMinutes: number; endMinutes: number } | null {
  if (columnDayYmd < startDate || columnDayYmd > endDate) return null;

  if (startDate === endDate && startDate === columnDayYmd) {
    const startMinutes = hmStringToMinutes(startTime);
    let endMinutes = hmStringToMinutes(endTime);
    if (endMinutes <= startMinutes) endMinutes += 24 * 60;
    return { startMinutes, endMinutes };
  }
  if (startDate === columnDayYmd) {
    return { startMinutes: hmStringToMinutes(startTime), endMinutes: 24 * 60 };
  }
  if (endDate === columnDayYmd) {
    const endMinutes = hmStringToMinutes(endTime);
    if (endMinutes <= 0) return null;
    return { startMinutes: 0, endMinutes };
  }
  if (startDate < columnDayYmd && columnDayYmd < endDate) {
    return { startMinutes: 0, endMinutes: 24 * 60 };
  }
  return null;
}

/** Whether an availability row overlaps a calendar column day (after timezone decode). */
export function availabilityRowAppliesOnCalendarDay(
  record: AvailabilityRowLike,
  dayYmd: string,
  calendarViewTimezone: string,
): boolean {
  const from = extractDatePartInTimezone(
    record.effective_from || '',
    calendarViewTimezone,
  );
  const to = record.effective_to
    ? extractDatePartInTimezone(record.effective_to, calendarViewTimezone)
    : '9999-12-31';
  if (!from || dayYmd < from || dayYmd > to) return false;
  return (
    availabilityRowToMinutesOnCalendarDay(record, dayYmd, calendarViewTimezone) !=
    null
  );
}

/**
 * Map an availability wall-clock range on conversionDateStr in sourceTimezone
 * to HH:mm (24h) in targetTimezone using full instants.
 *
 * Fixes broken chains that do convertTimeToUTC → convertTimeFromUTC with only
 * HH:MM strings, which drops the calendar date when the end instant crosses
 * midnight in UTC (e.g. 9:00–17:00 America/Los_Angeles stored as UTC times).
 */
export function availabilityWallRangeToHmInTimezone(
  conversionDateStr: string,
  startTimeRaw: string,
  endTimeRaw: string,
  sourceTimezone: string,
  targetTimezone: string,
): { startHm: string; endHm: string } | null {
  const startHmSlice = String(startTimeRaw || '').slice(0, 5);
  const endHmSlice = String(endTimeRaw || '').slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(startHmSlice) || !/^\d{2}:\d{2}$/.test(endHmSlice)) {
    return null;
  }
  const [sh, sm] = startHmSlice.split(':').map((n) => Number.parseInt(n, 10));
  const [eh, em] = endHmSlice.split(':').map((n) => Number.parseInt(n, 10));
  if (
    Number.isNaN(sh) ||
    Number.isNaN(sm) ||
    Number.isNaN(eh) ||
    Number.isNaN(em)
  ) {
    return null;
  }
  const src = (sourceTimezone || 'UTC').trim() || 'UTC';
  const tgt = (targetTimezone || 'UTC').trim() || 'UTC';

  let startMs = utcMsForZonedWallClock(conversionDateStr, sh, sm, src);
  let endMs = utcMsForZonedWallClock(conversionDateStr, eh, em, src);
  if (startMs == null || endMs == null) return null;
  if (endMs <= startMs) {
    endMs += 24 * 60 * 60 * 1000;
  }

  const hmFromMs = (ms: number, timeZone: string): string => {
    const parts = getCachedDateTimeFormat('en-CA', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(ms));
    const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
    const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
    return `${hour}:${minute}`;
  };

  return {
    startHm: hmFromMs(startMs, tgt),
    endHm: hmFromMs(endMs, tgt),
  };
}

/**
 * Resolve moderator availability wall times into `targetTimezone` (session type or calendar view),
 * aligned with the scheduler grid:
 * - Missing row timezone → interpret wall clock in `moderatorWallFallback` (working timezone).
 * - Row `UTC` with same-day hours like 09:00–17:00: treat digits as moderator-local (legacy saves),
 *   not UTC. Otherwise map UTC wall → target, with a pre-6am fallback retry.
 */
/**
 * Rows saved as timezone=UTC with "09:00"–"17:00" are almost always moderator
 * *local* hours (see TeamAvailability save path). Detect that "mistaken UTC"
 * weekday-workday shape so we can reinterpret in the moderator wall timezone.
 */
function isMistakenUtcLocalWorkday(
  startTimeRaw: string,
  endTimeRaw: string,
): boolean {
  const m1 = /^(\d{2}):(\d{2})$/.exec(String(startTimeRaw || '').slice(0, 5));
  const m2 = /^(\d{2}):(\d{2})$/.exec(String(endTimeRaw || '').slice(0, 5));
  if (!m1 || !m2) return false;
  const sh = Number.parseInt(m1[1], 10);
  const eh = Number.parseInt(m2[1], 10);
  return (
    !Number.isNaN(sh) &&
    !Number.isNaN(eh) &&
    sh >= 6 &&
    sh <= 11 &&
    eh > sh &&
    eh >= 11 &&
    eh <= 20
  );
}

/** Whether an HH:mm string falls before 6am (used for the UTC pre-6am fallback retry). */
function isBeforeSixAm(hm: string): boolean {
  const [h, m] = hm.split(':').map((n) => Number.parseInt(String(n), 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return false;
  return h * 60 + m < 6 * 60;
}

/** Empty / UTC / GMT — treat wall times as UTC unless legacy/moderator rules apply. */
const isUtcFamilyTz = (tz: string): boolean => {
  const u = tz.toUpperCase();
  return !tz || u === 'UTC' || u === 'GMT';
};

/** Moderator wall fallback is usable only if present and not itself UTC/GMT. */
const isUsefulModeratorWall = (modWall: string): boolean => {
  const u = modWall.toUpperCase();
  return modWall.length > 0 && u !== 'UTC' && u !== 'GMT';
};

/** Whether the moderator-wall retry produced a plausible daytime (05:00–20:00) start. */
const altStartIsDaytime = (alt: { startHm: string }): boolean => {
  const ah = Number.parseInt(alt.startHm.split(':')[0], 10);
  return !Number.isNaN(ah) && ah >= 5 && ah <= 20;
};

export function resolveAvailabilityRowToHmInTargetTimezone(
  conversionDateStr: string,
  startTimeRaw: string,
  endTimeRaw: string,
  rowTimezoneRaw: string | null | undefined,
  targetTimezone: string,
  moderatorWallFallback?: string | null,
): { startHm: string; endHm: string } | null {
  const rowTz = String(rowTimezoneRaw ?? '').trim();
  const isUtcFamily = isUtcFamilyTz(rowTz);
  const modWall = (moderatorWallFallback ?? '').trim();
  const modWallUseful = isUsefulModeratorWall(modWall);

  const wall = (tz: string) =>
    availabilityWallRangeToHmInTimezone(
      conversionDateStr,
      startTimeRaw,
      endTimeRaw,
      tz,
      targetTimezone,
    );

  // Explicit non–UTC-family IANA on the row (e.g. America/Los_Angeles). "utc" must not go here
  // or Intl may mis-resolve and skew PST grids.
  if (rowTz && !isUtcFamily) {
    return wall(rowTz);
  }

  if (!rowTz && modWall) {
    return wall(modWall);
  }

  if (
    rowTz &&
    isUtcFamily &&
    modWallUseful &&
    isMistakenUtcLocalWorkday(startTimeRaw, endTimeRaw)
  ) {
    return wall(modWall);
  }

  const primary = wall(isUtcFamily ? 'UTC' : rowTz);
  if (!primary) return null;

  // UTC times that resolve to pre-dawn hours are often misclassified; retry in the
  // moderator wall timezone and prefer it when it yields a plausible daytime start.
  if (isUtcFamily && modWallUseful && isBeforeSixAm(primary.startHm)) {
    const alt = wall(modWall);
    if (alt && altStartIsDaytime(alt)) return alt;
  }

  return primary;
}

/** HH:MM UTC wall clock to minutes since midnight; null if invalid. */
export const utcWallTimeToMinutes = (timeStr: string): number | null => {
  const parts = String(timeStr || '').split(':').map((n) => Number.parseInt(n, 10));
  const h = parts[0];
  const min = parts[1];
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  return h * 60 + min;
};

/**
 * Get user's browser timezone
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Get today's date in the user's local timezone (YYYY-MM-DD format).
 * Use this for upload dates so folder names and displayed dates match across timezones.
 * Avoids UTC date issues (e.g., US users uploading in evening seeing next-day folder).
 */
export const getLocalDateString = (): string => {
  return getTodayInTimezone();
};

/**
 * Get today's date in the user's timezone (YYYY-MM-DD format)
 * This ensures date validation is based on the user's local date, not UTC
 */
export const getTodayInTimezone = (timezone?: string): string => {
  const targetTimezone = timezone || getUserTimezone();
  const now = new Date();
  
  // Format the current date in the target timezone
  const formatter = getCachedDateTimeFormat('en-CA', {
    timeZone: targetTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  
  return formatter.format(now);
};

/**
 * Calendar date (YYYY-MM-DD) and wall time (HH:mm, 24h, zero-padded) for an UTC instant
 * in the given IANA timezone. Matches PM book session slot grid (session type timezone).
 */
/**
 * Single display line for a session range, e.g. "Apr 10, 2026 9:00 AM – 9:10 AM",
 * using the session type IANA timezone (aligned with book-page slot grid and modals).
 */
export function formatUtcRangeForDisplayInTimezone(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  ianaTimezone: string | null | undefined,
): string {
  if (!startIso?.trim()) return '—';
  const tz = ianaTimezone?.trim() || getUserTimezone();
  const start = new Date(startIso.trim());
  const end = endIso?.trim() ? new Date(endIso.trim()) : start;
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '—';
  try {
    const datePart = getCachedDateTimeFormat('en-US', {
      timeZone: tz,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(start);
    const timeOpts: Intl.DateTimeFormatOptions = getIntlTimeFormatOptions({
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
    });
    const t1 = getCachedDateTimeFormat('en-US', timeOpts).format(start);
    const t2 = getCachedDateTimeFormat('en-US', timeOpts).format(end);
    const abbr = getTimezoneAbbreviation(tz, start);
    return abbr
      ? `${datePart} | ${t1} - ${t2} ${abbr}`
      : `${datePart} | ${t1} - ${t2}`;
  } catch {
    return '—';
  }
}

export function utcIsoToDateAndTimeInTimezone(
  isoUtc: string | null | undefined,
  ianaTimezone: string | null | undefined,
): {date: string; time: string} | null {
  if (!isoUtc?.trim()) return null;
  const d = new Date(isoUtc.trim());
  if (Number.isNaN(d.getTime())) return null;
  const tz = ianaTimezone?.trim() || getUserTimezone();
  try {
    const dateStr = getCachedDateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
    const parts = getCachedDateTimeFormat('en-CA', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(d);
    const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
    const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
    const timeStr = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
    return {date: dateStr, time: timeStr};
  } catch {
    return null;
  }
}

/**
 * Get current time in a timezone as minutes from midnight (0-1439).
 * Use this when comparing slot times (which are in session-type timezone) to "now"
 */
export const getCurrentTimeMinutesInTimezone = (timezone: string): number => {
  const targetTimezone = timezone || getUserTimezone();
  const now = new Date();
  try {
    // Use formatToParts for reliable hour/minute in 24h (avoids locale string parsing)
    const formatter = getCachedDateTimeFormat('en-CA', {
      timeZone: targetTimezone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    const parts = formatter.formatToParts(now);
    const hour = Number.parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10) || 0;
    const minute = Number.parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10) || 0;
    return hour * 60 + minute;
  } catch {
    // Invalid timezone (e.g. "EST" instead of "America/New_York") – fall back to user's local time
    return now.getHours() * 60 + now.getMinutes();
  }
};

/**
 * Convert time from UTC to display timezone
 * @param timeStr - Time string in HH:MM format
 * @param targetTimezone - Target timezone (e.g., 'America/Los_Angeles')
 * @param dateStr - Optional date (YYYY-MM-DD) for DST calculation
 */
export const convertTimeFromUTC = (
  timeStr: string,
  targetTimezone: string,
  dateStr?: string
): string => {
  try {
    if (!timeStr || !targetTimezone) {
      return timeStr ? timeStr.split(':').slice(0, 2).join(':') : '00:00';
    }

    // Parse time string (HH:MM format, may include seconds)
    const [hours, minutes] = timeStr.split(':').slice(0, 2).map(Number);

    if (isNaN(hours) || isNaN(minutes)) {
      return timeStr.split(':').slice(0, 2).join(':');
    }

    // Use the provided date or default to today for DST calculation
    let utcDate: Date;
    if (dateStr && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Use the actual date from the availability record for accurate DST handling
      utcDate = new Date(
        Date.UTC(
          Number.parseInt(dateStr.split('-')[0]),
          Number.parseInt(dateStr.split('-')[1]) - 1,
          Number.parseInt(dateStr.split('-')[2]),
          hours,
          minutes,
          0
        )
      );
    } else {
      // Fallback to today if no date provided
      const today = new Date();
      utcDate = new Date(
        Date.UTC(
          today.getUTCFullYear(),
          today.getUTCMonth(),
          today.getUTCDate(),
          hours,
          minutes,
          0
        )
      );
    }

    // Convert to target timezone using Intl API (automatically handles DST)
    const formatter = getCachedDateTimeFormat('en-US', {
      timeZone: targetTimezone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });

    const parts = formatter.formatToParts(utcDate);
    const hour = parts.find((p) => p.type === 'hour')?.value || '00';
    const minute = parts.find((p) => p.type === 'minute')?.value || '00';

    return `${hour}:${minute}`;
  } catch (error) {
    console.error('Error converting time from UTC:', error, {
      timeStr,
      targetTimezone,
      dateStr,
    });
    return timeStr ? timeStr.split(':').slice(0, 2).join(':') : '00:00';
  }
};

/**
 * Convert time from user's timezone to UTC
 * @param timeStr - Time string in HH:MM format
 * @param sourceTimezone - Source timezone (e.g., 'America/Los_Angeles')
 * @param dateStr - Optional date (YYYY-MM-DD) for DST calculation
 */
export const convertTimeToUTC = (
  timeStr: string,
  sourceTimezone: string,
  dateStr?: string
): string => {
  try {
    if (!timeStr || !sourceTimezone) {
      return timeStr ? timeStr.split(':').slice(0, 2).join(':') : '00:00';
    }

    // Parse time string (HH:MM format)
    const [hours, minutes] = timeStr.split(':').slice(0, 2).map(Number);

    if (isNaN(hours) || isNaN(minutes)) {
      return timeStr.split(':').slice(0, 2).join(':');
    }

    // Use the provided date or get today's date in source timezone for DST calculation
    let year: number, month: number, day: number;
    if (dateStr && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Use the actual date the user entered
      [year, month, day] = dateStr.split('-').map(Number);
    } else {
      // Fallback to today's date in source timezone
      const today = new Date();
      const dateFormatter = getCachedDateTimeFormat('en-CA', {
        timeZone: sourceTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const sourceDateStr = dateFormatter.format(today);
      [year, month, day] = sourceDateStr.split('-').map(Number);
    }

    // Create a date-time string and use iterative approach to find correct UTC time
    const targetTotalMinutes = hours * 60 + minutes;

    // Start with a guess: assume the time is at noon UTC on that date
    let testDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

    // Format in source timezone to see what time it represents
    const sourceFormatter = getCachedDateTimeFormat('en-US', {
      timeZone: sourceTimezone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });

    let sourceParts = sourceFormatter.formatToParts(testDate);
    let sourceHour = Number.parseInt(sourceParts.find((p) => p.type === 'hour')?.value || '0');
    let sourceMin = Number.parseInt(sourceParts.find((p) => p.type === 'minute')?.value || '0');
    let sourceTotalMinutes = sourceHour * 60 + sourceMin;

    // Calculate the offset needed
    let offsetMinutes = targetTotalMinutes - sourceTotalMinutes;

    // Adjust the date by the offset
    testDate = new Date(testDate.getTime() + offsetMinutes * 60 * 1000);

    // Verify and refine (handle day rollover)
    sourceParts = sourceFormatter.formatToParts(testDate);
    sourceHour = Number.parseInt(sourceParts.find((p) => p.type === 'hour')?.value || '0');
    sourceMin = Number.parseInt(sourceParts.find((p) => p.type === 'minute')?.value || '0');
    sourceTotalMinutes = sourceHour * 60 + sourceMin;

    // If still not matching, adjust again (for edge cases)
    if (Math.abs(sourceTotalMinutes - targetTotalMinutes) > 1) {
      offsetMinutes = targetTotalMinutes - sourceTotalMinutes;
      testDate = new Date(testDate.getTime() + offsetMinutes * 60 * 1000);
    }

    // Now format in UTC to get the UTC time
    const utcFormatter = getCachedDateTimeFormat('en-US', {
      timeZone: 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    const utcParts = utcFormatter.formatToParts(testDate);
    const utcHour = Number.parseInt(utcParts.find((p) => p.type === 'hour')?.value || '0');
    const utcMin = Number.parseInt(utcParts.find((p) => p.type === 'minute')?.value || '0');

    return `${String(utcHour).padStart(2, '0')}:${String(utcMin).padStart(2, '0')}`;
  } catch (error) {
    console.error('Error converting time to UTC:', error, {
      timeStr,
      sourceTimezone,
      dateStr,
    });
    return timeStr ? timeStr.split(':').slice(0, 2).join(':') : '00:00';
  }
};

/**
 * Convert date from UTC to display timezone
 */
export const convertDateFromUTC = (
  dateStr: string,
  targetTimezone: string
): string => {
  try {
    if (!dateStr || !targetTimezone) {
      return dateStr || '';
    }

    // Clean the date string (remove any time portion if present)
    let cleanDateStr = dateStr.split('T')[0].split(' ')[0];

    // Handle different date formats
    let date: Date;
    if (cleanDateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // YYYY-MM-DD format - treat as UTC date at noon to avoid DST issues
      date = new Date(cleanDateStr + 'T12:00:00Z');
    } else if (cleanDateStr.includes('T')) {
      // ISO format with time - parse as UTC
      date = new Date(cleanDateStr);
    } else {
      // Try parsing as-is
      date = new Date(cleanDateStr);
    }

    if (isNaN(date.getTime())) {
      return cleanDateStr;
    }

    // Convert to target timezone
    const formatter = getCachedDateTimeFormat('en-CA', {
      timeZone: targetTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const formatted = formatter.format(date);

    if (!formatted || formatted.trim() === '') {
      return cleanDateStr;
    }

    return formatted;
  } catch (error) {
    console.error('Error converting date from UTC:', error, {
      dateStr,
      targetTimezone,
    });
    return dateStr ? dateStr.split('T')[0].split(' ')[0] : '';
  }
};

/**
 * Map a calendar day (Y-M-D) in `sourceTimezone` to the UTC calendar date of the
 * same instant where that civil date is visible in `sourceTimezone` (aligned at ~noon local).
 *
 * Does not use `new Date('YYYY-MM-DDTHH:mm:ss')` without a zone — that parses as the
 * **browser's** local timezone and breaks when it differs from `sourceTimezone`.
 */
export const convertDateToUTC = (
  dateStr: string,
  sourceTimezone: string
): string => {
  try {
    const trimmed = dateStr.trim();
    const dm = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!dm || !sourceTimezone) {
      return trimmed;
    }

    const y = Number(dm[1]);
    const mo = Number(dm[2]);
    const d = Number(dm[3]);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) {
      return trimmed;
    }

    const formatter = getCachedDateTimeFormat('en-US', {
      timeZone: sourceTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });

    const civilAt = (ms: number) => {
      const parts = formatter.formatToParts(new Date(ms));
      const gv = (ty: Intl.DateTimeFormatPartTypes) =>
        Number(parts.find((p) => p.type === ty)?.value ?? 'NaN');
      return { cy: gv('year'), cm: gv('month'), cd: gv('day') };
    };

    const DAY = 86400000;
    let t = Date.UTC(y, mo - 1, d, 12, 0, 0);
    let { cy, cm, cd } = civilAt(t);
    let guard = 0;
    while ((cy !== y || cm !== mo || cd !== d) && guard++ < 400) {
      if (cy > y || (cy === y && cm > mo) || (cy === y && cm === mo && cd > d)) {
        t -= DAY;
      } else {
        t += DAY;
      }
      ({ cy, cm, cd } = civilAt(t));
    }

    const u = new Date(t);
    return `${u.getUTCFullYear()}-${String(u.getUTCMonth() + 1).padStart(2, '0')}-${String(
      u.getUTCDate()
    ).padStart(2, '0')}`;
  } catch (error) {
    console.error('Error converting date to UTC:', error);
    return dateStr;
  }
};

/**
 * Format date for input field (YYYY-MM-DD)
 */
export const formatDateForInput = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return '';

  try {
    // If already in YYYY-MM-DD format, return as-is (no timezone conversion needed)
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }

    // If it's an ISO datetime string, extract just the date part (YYYY-MM-DD)
    // This avoids timezone conversion issues
    if (dateString.includes('T')) {
      const datePart = dateString.split('T')[0];
      if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return datePart;
      }
    }

    // If it's a date string with spaces or other separators, try to extract YYYY-MM-DD
    // Match patterns like "2025-12-12", "2025/12/12", etc.
    const dateMatch = dateString.match(/(\d{4})[-\/](\d{2})[-\/](\d{2})/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return `${year}-${month}-${day}`;
    }

    // Fallback: try parsing with Date, but use UTC methods to avoid timezone shifts
    // This should rarely be needed if dates are stored correctly
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }

    // Use UTC methods to avoid timezone conversion
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

/**
 * Format date range helper (converts from UTC to display timezone)
 */
/** "Mon D, YYYY" from a raw date string; null if it can't be parsed. */
const rawDateToMonDayYear = (from: string): string | null => {
  const d = new Date(from);
  if (Number.isNaN(d.getTime())) return null;
  const month = d.toLocaleString('default', { month: 'short' });
  return `${month} ${d.getDate()}, ${d.getFullYear()}`;
};

/** Convert from/to to YYYY-MM-DD in the target timezone, anchoring on startTime when given. */
const convertRangeDatesToYmd = (
  from: string,
  to: string | null,
  timezone: string,
  startTime?: string
): { fromDateStr: string; toDateStr: string | null } => {
  if (startTime && from.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [hours, minutes] = startTime.split(':').slice(0, 2).map(Number);
    const hhmm = `T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00Z`;
    const formatter = getCachedDateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return {
      fromDateStr: formatter.format(new Date(from + hhmm)),
      toDateStr: to ? formatter.format(new Date(to + hhmm)) : null,
    };
  }
  return {
    fromDateStr: convertDateFromUTC(from, timezone),
    toDateStr: to ? convertDateFromUTC(to, timezone) : null,
  };
};

export const formatDateRange = (
  from: string,
  to: string | null,
  timezone: string,
  startTime?: string
): string => {
  try {
    if (!from || !timezone) {
      return 'N/A';
    }

    const { fromDateStr, toDateStr } = convertRangeDatesToYmd(from, to, timezone, startTime);

    if (!fromDateStr || fromDateStr.trim() === '') {
      return rawDateToMonDayYear(from) ?? 'N/A';
    }

    // Parse the converted date strings (they should be in YYYY-MM-DD format)
    const fromDate = new Date(fromDateStr + 'T00:00:00');
    const toDate = toDateStr ? new Date(toDateStr + 'T00:00:00') : null;

    if (isNaN(fromDate.getTime())) {
      return rawDateToMonDayYear(from) ?? 'N/A';
    }

    const formatDateShort = (date: Date) => {
      const month = date.toLocaleString('default', { month: 'short' });
      return `${month} ${date.getDate()}`;
    };
    const formatDateFull = (date: Date) => {
      const month = date.toLocaleString('default', { month: 'short' });
      return `${month} ${date.getDate()}, ${date.getFullYear()}`;
    };

    if (toDate && !isNaN(toDate.getTime()) && toDate.getTime() !== fromDate.getTime()) {
      const year = fromDate.getFullYear();
      return `${formatDateShort(fromDate)} - ${formatDateShort(toDate)}, ${year}`;
    }
    return formatDateFull(fromDate);
  } catch (error) {
    console.error('Error formatting date range:', error, { from, to, timezone });
    // Last resort: try to format the original date string
    try {
      return rawDateToMonDayYear(from) ?? 'N/A';
    } catch (e) {
      console.warn('Fallback date formatting also failed:', (e as any)?.message || e);
      return 'N/A';
    }
  }
};

const validEpochMs = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

/**
 * datetime-local value (`YYYY-MM-DDTHH:mm`) representing an instant in a
 * specified IANA timezone — shared by the moderator's Adjust Start/End Time
 * fields and the PM's Approved Start/End Time fields so both use the exact
 * same wall-clock-in-timezone control.
 */
export function toZonedDateTimeInputValue(
  value: string | null | undefined,
  timeZone: string,
): string {
  const timestamp = validEpochMs(value);
  if (timestamp === null) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(timestamp));
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
  } catch {
    return '';
  }
}

/** Converts a datetime-local wall clock in a specified IANA timezone to UTC ISO. */
export function zonedDateTimeInputToIso(
  value: string,
  timeZone: string,
): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  const ymd = `${year}-${month}-${day}`;
  try {
    const utcMs = utcMsForZonedWallClock(ymd, Number(hour), Number(minute), timeZone);
    return utcMs === null ? null : new Date(utcMs).toISOString();
  } catch {
    return null;
  }
}

